import { Box, Typography, useTheme, CircularProgress } from "@mui/material";
import { styled, useMediaQuery } from "@mui/system";
import useStyles from "./styles";
import handleColors from "../../utils/handleColors";
import TooltipMui from "../../../tooltipMui";
import { MyCircularProgressConfig } from "./types";
import maskUnits from "../../../../utils/maskUnits";
import React, { useEffect, useState } from "react";

const StyledCircularProgress = styled(CircularProgress)(() => ({
  "& .MuiCircularProgress-circle": {
    strokeLinecap: "round",
  },
}));

const formatSeries = (data: MyDatum[], indexBy: string, keys: string[]) =>
  data.map((item) => ({
    label: item[indexBy],
    value: item[keys[0]],
  }));
const CustomCircularProgress: React.FC<MyCircularProgressConfig> = (props) => {
  const {
    data,
    colorsByKey,
    keys,
    indexBy,
    orientation = "horizontal",
    units,
    fractionDigits,
  } = props;
  const formattedData =
    keys && keys.length > 0 ? formatSeries(data, indexBy, keys) : [];
  const [boxHeight, setBoxHeight] = useState<number | null>(null);
  const [boxWidth, setBoxWidth] = useState<number | null>(null);
  const [activeBreakpoint, setActiveBreakpoint] = useState<string | null>(null);
  const [box2Height, setBox2Height] = useState<number | null>(null);
  const [box2Width, setBox2Width] = useState<number | null>(null);

  const boxRef = React.useRef(null);
  const box2Ref = React.useRef(null);

  const theme = useTheme();
  const styles = useStyles(theme);

  const handleResize = () => {
    if (boxRef.current) {
      const { offsetHeight, offsetWidth } = boxRef.current;
      setBoxHeight(offsetHeight);
      setBoxWidth(offsetWidth);
    }
    if (box2Ref.current) {
      const { offsetHeight, offsetWidth } = box2Ref.current;
      setBox2Height(offsetHeight);
      setBox2Width(offsetWidth);
    }
  };
  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [boxRef.current, box2Ref.current]);

  const valueTotal = formattedData.reduce(
    (acc: number, item) => acc + Number(item.value),
    0
  );

  const breakPointOrder = ["xs", "sm", "md", "mdl", "lg", "xl"];

  function getClosestBreakpoint(
    breakpoints: Record<string, string> | string,
    current: string | null
  ): string | undefined {
    if (!current) return;
    if (typeof breakpoints === "object") {
      const index = breakPointOrder.indexOf(current);
      for (let i = index; i >= 0; i--) {
        const key = breakPointOrder[i];
        if (breakpoints[key]) {
          return breakpoints[key];
        }
      }
    }
    return breakpoints as string;
  }

  const breakpoints = {
    xs: useMediaQuery(theme.breakpoints.only("xs")),
    sm: useMediaQuery(theme.breakpoints.only("sm")),
    md: useMediaQuery(theme.breakpoints.only("md")),
    mdl: useMediaQuery(theme.breakpoints.only("mdl")),
    lg: useMediaQuery(theme.breakpoints.only("lg")),
    xl: useMediaQuery(theme.breakpoints.only("xl")),
  };

  useEffect(() => {
    for (const [key, value] of Object.entries(breakpoints)) {
      if (value) {
        setActiveBreakpoint(key);
        break;
      }
    }
  }, [breakpoints]);

  const itemSize = (gap: number) =>
    getClosestBreakpoint(orientation, activeBreakpoint) === "horizontal"
      ? {
          width: boxWidth && (boxWidth - gap * (formattedData?.length - 1)) / 3,
          height: "100%",
        }
      : {
          height:
            boxHeight &&
            (boxHeight - gap * (formattedData?.length - 1)) / 3 + "px",
        };

  return (
    <Box
      sx={styles.boxContainer(orientation === "horizontal" ? "row" : "column")}
      ref={boxRef}
    >
      {formattedData.map((item, index) => (
        <TooltipMui
          key={item.label + "" + index}
          color={
            colorsByKey
              ? handleColors(item.label, theme, colorsByKey)
              : theme.palette.primary.main
          }
          valueX={item.label}
          valueY={maskUnits(Number(item.value) / valueTotal, "%", 0)}
          valueZ={maskUnits(item.value, units, fractionDigits)}
        >
          <Box sx={styles.boxCircularAndText(boxHeight, boxWidth)}>
            {boxWidth && (itemSize(16).width as number) <= 100 ? (
              <>
                <Box
                  sx={styles.boxText(
                    orientation === "horizontal" ? "row" : "column"
                  )}
                >
                  <Box sx={styles.boxTextsValues}>
                    <Typography
                      variant="caption"
                      component="div"
                      sx={{ color: "background.primary", fontSize: 20 }}
                    >
                      {maskUnits(
                        item.value ? Number(item.value ?? 0) / valueTotal : 0,
                        "%",
                        0
                      )}
                    </Typography>

                    <Box
                      sx={styles.symbolBox(
                        colorsByKey
                          ? handleColors(item.label, theme, colorsByKey)
                          : undefined
                      )}
                    />
                    <Typography sx={{ color: "text.primary", fontSize: 18 }}>
                      {maskUnits(item.value, units, fractionDigits)}
                    </Typography>
                  </Box>
                  <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
                    {item.label}
                  </Typography>
                </Box>
              </>
            ) : (
              <>
                <Box
                  sx={[
                    {
                      position: "relative",
                    },
                    itemSize(16),
                  ]}
                  ref={box2Ref}
                >
                  <CircularProgress
                    variant="determinate"
                    value={100}
                    size={box2Height + "px"}
                    thickness={6}
                    sx={{
                      color: "background.textField",
                      position: "absolute",
                    }}
                  />
                  <StyledCircularProgress
                    variant="determinate"
                    value={(Number(item.value) / Number(valueTotal)) * 100}
                    size={box2Height + "px"}
                    thickness={6}
                    sx={{
                      color: colorsByKey
                        ? handleColors(item.label, theme, colorsByKey)
                        : undefined,
                    }}
                  />
                  <Box sx={styles.boxTextCenter(box2Height as number)}>
                    <Typography
                      variant="caption"
                      component="div"
                      sx={{ color: "background.primary", fontSize: 16 }}
                    >
                      {maskUnits(
                        item.value ? Number(item.value ?? 0) / valueTotal : 0,
                        "%",
                        0
                      )}
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={styles.boxText(
                    orientation === "horizontal" ? "row" : "column"
                  )}
                >
                  <Typography sx={{ color: "text.primary", fontSize: 20 }}>
                    {maskUnits(item.value, units, fractionDigits)}
                  </Typography>
                  <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
                    {item.label}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </TooltipMui>
      ))}
    </Box>
  );
};
export default CustomCircularProgress;
